import {
  MecColour,
  MecProduct,
  MecSize,
  MecVariant,
} from '@adapters/bigcommerce/types/product';

/**
 * Pure variant-availability derivation over a full {@link MecProduct}.
 *
 * Extracted and generalized from the PDP context (`context/PdpContext/index.tsx`
 * ~lines 321-405) so the same colour/size/variant resolution can be reused
 * outside the PDP (Quick Add on the PLP, hotspots, recommendations). PdpContext
 * is intentionally left untouched in ED-322; a follow-up can migrate it onto
 * these helpers.
 *
 * Gift-card bypass is preserved: gift cards never mark a colour or size as
 * unavailable, matching PdpContext behaviour.
 *
 * `hasStockAvailable` is the stock signal; `availableForSaleOnline` is the
 * merchandising signal. Both are kept distinct, as PdpContext does.
 */

/**
 * Minimal structural product these helpers need. Both the full `MecProduct`
 * and the slim `QuickAddProduct` projection satisfy it.
 */
export type VariantAvailabilityProduct = Pick<
  MecProduct,
  'colours' | 'sizes' | 'variants' | 'isGiftCard' | 'availableForSaleOnline'
>;

export interface ColourOption extends MecColour {
  hasAnyStock: boolean;
}

export interface SizeOption extends MecSize {
  /** A variant exists for the selected colour AND that variant has stock. */
  isAvailableForSelectedColour: boolean;
  /** A variant row exists for the selected colour at all (regardless of stock). */
  existsForSelectedColour: boolean;
  /**
   * The matched variant's `availableForSaleOnline` — distinct from stock:
   * some in-stock items are call-to-order only (BigCommerce's
   * `available_online_purchase`, surfaced on the PDP via
   * `shippingRestrictions`). Selectable only when both this and
   * {@link isAvailableForSelectedColour} are true.
   */
  isPurchasableOnline: boolean;
}

export function getColourOptions(
  product: VariantAvailabilityProduct
): ColourOption[] {
  const variants = product.variants ?? [];

  return (product.colours ?? []).map((colour) => ({
    ...colour,
    hasAnyStock: product.isGiftCard
      ? true
      : variants.some(
          (variant) =>
            variant.colourId === colour.id && variant.hasStockAvailable
        ),
  }));
}

export function getSizeOptionsForColour(
  product: VariantAvailabilityProduct,
  colourId: number | null
): SizeOption[] {
  // One-size products have no sizes to choose from.
  if (!product.sizes?.length) return [];

  // Without a selected colour we cannot resolve per-colour stock; treat all
  // sizes as present/available (the consumer resolves an initial colour first).
  if (colourId == null) {
    return product.sizes.map((size) => ({
      ...size,
      isAvailableForSelectedColour: true,
      existsForSelectedColour: true,
      isPurchasableOnline: true,
    }));
  }

  const variants = product.variants ?? [];

  const availableSizeIds = variants.flatMap((variant) =>
    variant.colourId === colourId && variant.hasStockAvailable
      ? [variant.sizeId]
      : []
  );

  const existingSizeIds = variants.flatMap((variant) =>
    variant.colourId === colourId ? [variant.sizeId] : []
  );

  const purchasableSizeIds = variants.flatMap((variant) =>
    variant.colourId === colourId && variant.availableForSaleOnline
      ? [variant.sizeId]
      : []
  );

  return product.sizes.map((size) => ({
    ...size,
    isAvailableForSelectedColour: product.isGiftCard
      ? true
      : availableSizeIds.includes(size.id),
    existsForSelectedColour: existingSizeIds.includes(size.id),
    isPurchasableOnline: product.isGiftCard
      ? true
      : purchasableSizeIds.includes(size.id),
  }));
}

/**
 * One-size (empty `sizes`): match on `colourId` alone. Otherwise both a colour
 * and a size are required and the variant must match both.
 */
export function resolveVariant(
  product: VariantAvailabilityProduct,
  colourId: number | null,
  sizeId: number | null
): MecVariant | undefined {
  if (colourId == null) return undefined;

  const hasSizes = !!product.sizes?.length;
  if (hasSizes && sizeId == null) return undefined;

  return (product.variants ?? []).find(
    (variant) =>
      variant.colourId === colourId && (!hasSizes || variant.sizeId === sizeId)
  );
}

/**
 * Fallback chain, matching PdpContext: selected variant's `availableForSaleOnline`
 * → selected colour's `availableForSaleOnline` → product `availableForSaleOnline`.
 */
export function getSelectedAvailability(
  product: VariantAvailabilityProduct,
  variant: MecVariant | undefined,
  colourId: number | null
): boolean {
  if (variant) return variant.availableForSaleOnline;

  const colour =
    colourId != null
      ? (product.colours ?? []).find((c) => c.id === colourId)
      : undefined;
  if (colour) return colour.availableForSaleOnline;

  return product.availableForSaleOnline ?? false;
}

/**
 * Resolve the colour to pre-select when variant data first loads.
 *
 * Priority: explicit `colourId` → `colourName` (case-insensitive; Algolia
 * variant keys are colour names) → colour-level SKU prefix match against
 * `MecVariant.sku` → single colour → `isDefault` → first colour (mirrors the
 * PDP's `getInitialColourId`; `null` only when there are no colours at all).
 */
export function resolveInitialColourId(
  product: VariantAvailabilityProduct,
  hint?: { colourId?: number; colourName?: string; colourSku?: string }
): number | null {
  const colours = product.colours ?? [];
  if (!colours.length) return null;

  if (hint?.colourId != null && colours.some((c) => c.id === hint.colourId)) {
    return hint.colourId;
  }

  if (hint?.colourName) {
    const target = hint.colourName.trim().toLowerCase();
    const byName = colours.find((c) => c.name?.trim().toLowerCase() === target);
    if (byName) return byName.id;
  }

  if (hint?.colourSku) {
    // The colour-level SKU is a prefix of the size-level variant SKUs.
    const variant = (product.variants ?? []).find(
      (v) =>
        v.sku &&
        v.sku.startsWith(hint.colourSku as string) &&
        v.colourId != null
    );
    if (variant?.colourId != null) return variant.colourId;
  }

  if (colours.length === 1) return colours[0].id;

  const byDefault = colours.find((c) => c.isDefault);
  if (byDefault) return byDefault.id;

  // Mirrors the PDP's `getInitialColourId` (utils/pdp/getInitialColourId.ts):
  // with no hint and no default, pre-select the first colour so a size grid
  // has something to filter by immediately, rather than leaving the shopper
  // with no colour selected at all.
  return colours[0].id;
}
